import React, {useState, useEffect} from 'react';
import { Stack, Typography, Checkbox, FormControlLabel, FormGroup, Button, InputAdornment, Link, useTheme } from '@mui/material';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { DateField, FormProvider, TextField } from 'components/Form';
import { useAuth } from 'lib/auth';
import { AdditionalPeriods } from 'components/AdditionalPeriod/AdditionalPeriod';
import { ImageAvatar } from 'components/Avatar';
import { Link as RouterLink } from 'react-router-dom';
import { paths } from 'routes';
import { useLocale } from 'i18n/useLocale';
import { LocalizedLink } from 'website/components/LocalizedLink';
import { DELEGON_URL } from 'configuration';
import { MoreOptionsButton } from './MoreOptionsButton';
import { isoToDateString } from 'utils/dateFormatter';
import { CompanyProfile } from 'components/CompanyProfile/CompanyProfile';
import { SmallProfile } from 'components/CompanyProfile/SmallProfile';
import { RedMessageBar } from 'components/RedMessageBar';
import { LinkedinIcon, LinkedinShareButton } from 'react-share';
import { getJobAdLocations } from 'utils/jobAdLocations';
import { StaticLocationMap } from 'components/JobAdsMap/StaticLocationMap';

const detailsTabName = 'details';

const getDefaultValues = () => ({
  agreement: null,
  buyer: null,
  dps: null,
  headline: '',
  role: '',
  experienceLevel: '',
  description: '',
  type: 'On site',
  hoursPerWeek: 40,
  maxRate: '',
  public_id: '',
  referenceNumber: '',
  startDate: null,
  endDate: null,
  applicationType: 'email',
  applicationEmail: '',
  applicationURL: '',
  lastDayToRespond: null,
  // agreementOrDpsName: '',
  buyerName: '',
  dpsName: '',
  locations: [],
  additionalPeriods: [],
  competenceAreas: [],
  supplierID: null,
  buyerLogoURL: '',
});

export const DetailsTab = ({ currentTab, jobAd }) => {
  const theme = useTheme(); 
  const { t } = useTranslation();
  const lang = useLocale();
  const { account } = useAuth(); 
  const [ loading, setLoading ] = useState(true);
  const [ additionalPeriods, setAdditionalPeriods ] = useState(jobAd?.additional_periods);
  const [ initialPeriod, setInitialPeriod ] = useState([
    {
      label: "Initial",
      startDate: jobAd?.start_date,
      endDate: jobAd?.end_date,
      status: "used",
    }
  ]);

  const lastDayToRespond = new Date(jobAd.last_day_to_respond);
  lastDayToRespond.setHours(23, 59, 59, 999);
  const isExpired = lastDayToRespond.getTime() < new Date().getTime();
  const isWithdrawn = jobAd.status === 'withdrawn';
  const isJobAdActive = !isExpired && !isWithdrawn;

  const methods = useForm({
    defaultValues: getDefaultValues(),
  });

  useEffect(() => {
    const {
      headline,
      buyer_id,
      buyer_name,
      buyer_logo_url,
      fa_id,
      fa_name,
      dps_id,
      dps_name,
      role,
      experience_level,
      description,
      type,
      hours_per_week,
      max_rate,
      reference_number,
      public_id,
      locations,
      places,
      competence_areas,
      start_date,
      end_date,
      additional_periods,
      application_method,
      application_email,
      application_url,
      last_day_to_respond,
      supplier_id,
    } = jobAd;   
  
    methods.reset({
      agreement: fa_id || dps_id,
      buyer: buyer_id,
      dps: dps_id,
      headline: headline,
      role: role,
      experienceLevel: experience_level,
      description: description,
      type: type,
      hoursPerWeek: hours_per_week,
      maxRate: max_rate,
      referenceNumber: reference_number,
      public_id: public_id,
      locations: getJobAdLocations({ locations, places }),
      competenceAreas: competence_areas,
      startDate: new Date(start_date),
      endDate: new Date(end_date),
      additionalPeriods: additional_periods,
      applicationType: application_method,
      applicationEmail: application_email,
      applicationURL: application_url,
      lastDayToRespond: new Date(last_day_to_respond),
      supplierID: supplier_id,
      // agreementOrDpsName: fa_name ? `${fa_name} (Agreement)` : dps_name ? `${dps_name} (DPS)` : '',
      buyerName: buyer_name,
      dpsName: dps_name,
      buyerLogoURL: buyer_logo_url,
    });
  
    setAdditionalPeriods(jobAd?.additional_periods);
    const tempPeriod = [...initialPeriod];
    tempPeriod[0].startDate = jobAd?.start_date;
    tempPeriod[0].endDate = jobAd?.end_date;
    setInitialPeriod(tempPeriod);
    setLoading(false);
  }, [jobAd]);

  const onSubmit = () => {};

  if (currentTab !== detailsTabName) return null;

  if (loading) {
    return null;
  }

  return (
    <FormProvider methods={methods} onSubmit={onSubmit}>
      <Stack direction={{xs: "column", md: "row"}} width="100%" height="100%" bgcolor={'background.paper'} gap={2}>
        <Stack
          sx={{
            flex: 2,
            flexDirection: "column",
            gap: 2,
          }}
        >
          <Stack
            sx={{
              flex: 2,
              flexDirection: "column",
              gap: 2,
              border: "1px solid",
              borderRadius: 1,
              borderColor: "border.jobAd",
              padding: 2,
            }}
          >
            <Stack
              direction={"row"}
              gap={2}
              alignItems={"center"}
            >
              {jobAd?.buyer_hidden ? (
                <ImageAvatar anonymous name={jobAd?.buyer_name || ''} />
              ) : (
                <Link
                  component={RouterLink}
                  to={paths.public.companyPublicProfile("buyer", jobAd?.buyer_slug, lang)}
                  color="inherit"
                  underline="none"
                >
                  <ImageAvatar img={jobAd?.buyer_logo_url || ''} name={jobAd?.buyer_name || ''} />
                </Link>
              )}
              <Typography variant="h5" fontWeight={600} fontSize={'1.5rem'} lineHeight={"32px"} color='text.black'>{jobAd?.headline}</Typography>
              </Stack>
              {!isJobAdActive && (
              <Link component={LocalizedLink} to={paths.website.jobAds} color="inherit" underline="none">
                <RedMessageBar
                  sx={{
                    minHeight: 80,
                  }}
                  message={
                    `${t(isWithdrawn ? 'website:jobDetails.withdrawnNotice' : 'website:jobDetails.expiredNotice')} ${t('website:jobDetails.seeOtherJobAds')}`
                  }
                />
              </Link>
              )}
              <TextField
              name="description"
              disabled
              type="text"
              fullWidth
              sx={{
                '& .MuiInputBase-root, .MuiInputBase-input': {
                  p: 0,
                  border: 'none!important',
                  backgroundColor: "background.paper",
                  py: "0px!important",
                  px: "0px!important",
                },
              }}
              multiline
              minRows={2}
              wrapperSx={{
                gap: 0,
              }}
            />
          </Stack>
          {!jobAd?.buyer_hidden && jobAd?.buyer_name?.length > 0 && jobAd?.buyer_description?.length > 0 && (
            <CompanyProfile company_name={jobAd?.buyer_name} square_logo_url={jobAd?.buyer_logo_url} banner_logo_url={jobAd?.buyer_banner_url} description={jobAd?.buyer_description} slug={jobAd?.buyer_slug} company_id={jobAd?.buyer_id} />
          )}
          </Stack>
        <Stack
          sx={{
            flex: 1,
            flexDirection: "column",
            gap: 2,
          }}
        >
          <Stack
            sx={{
              flexDirection: "column",
              gap: 2.5,
              border: "1px solid",
              borderRadius: 1,
              borderColor: "border.jobAd",
              padding: 2,
              backgroundColor: "background.jobAd",
              alignSelf: "stretch"
            }}
          >
            {((jobAd.application_method === 'email' && jobAd?.application_email?.length > 0) || jobAd.application_method === 'in_app') && (
              <MoreOptionsButton adId={jobAd.ad_id} disabled={!isJobAdActive} />
            )}
            {jobAd.application_method === 'url' && jobAd?.application_url?.length > 0 && (
              <Button
                size="medium"
                variant="contained"
                color="primary"
                sx={{ mb: 0.5, width: 1, maxWidth: 300, alignSelf: "center"}}
                href={jobAd?.application_url}
                target={"_blank"}
                rel={"noopener noreferrer"}
                disabled={!isJobAdActive}
              >
                {t('website:jobDetails.applyByUrl')}
              </Button>
            )}
            <Typography
              sx={{
                borderTop: "1px solid",
                borderColor: "#E4E5E8",
              }}
            ></Typography>
            <Typography
              variant='body3'
              sx={{
                color: "text.black",
                fontFeatureSettings: `'liga' off, 'clig' off`,
              }}
            >
              {t('website:jobDetails.allDetails')}
            </Typography>
            <TextField
              name="referenceNumber"
              label={t('website:jobDetails.fields.referenceNumber')}
              type="text"
              wrapperSx={{
                '& .MuiInputBase-root-MuiOutlinedInput-root.Mui-disabled, .Mui-disabled': {
                  backgroundColor: 'background.jobAd',
                  border: 'none',
                  pl: 0,
                },
                '& .MuiInputBase-input.Mui-disabled': {
                  px: '0!important',
                  py: '4px!important',
                  border: "none",
                }
              }}
              disabled
            />
            <TextField
              name="role"
              label={t('website:jobDetails.fields.role')}
              type="text"
              wrapperSx={{
                '& .MuiInputBase-root-MuiOutlinedInput-root.Mui-disabled, .Mui-disabled': {
                  backgroundColor: 'background.jobAd',
                  border: 'none!important',
                  pl: 0,
                },
                '& .MuiInputBase-input.Mui-disabled': {
                  px: '0!important',
                  py: '4px!important',
                  border: "none!important",
                },
              }}
              disabled
            />
            <TextField
              name="experienceLevel"
              label={t('website:jobDetails.fields.experienceLevel')}
              type="text"
              wrapperSx={{
                '& .MuiInputBase-root-MuiOutlinedInput-root.Mui-disabled, .Mui-disabled': {
                  backgroundColor: 'background.jobAd',
                  border: 'none',
                  pl: 0,
                },
                '& .MuiInputBase-input.Mui-disabled': {
                  px: '0!important',
                  py: '4px!important',
                  border: "none",
                }
              }}
              disabled
            />
            <DateField
              name="lastDayToRespond"
              label={t('website:jobDetails.fields.applicationDeadline')}
              type="text"
              wrapperSx={{
                '& .MuiInputBase-root-MuiOutlinedInput-root.Mui-disabled, .Mui-disabled, .MuiFormControl-root-MuiTextField-root': {
                  backgroundColor: 'background.jobAd',
                  border: 'none!important',
                  px: 0,
                },
                '& .MuiInputBase-input.Mui-disabled': {
                  px: '0!important',
                  py: '4px!important',
                  border: "none!important",
                },
              }}
              disabled
            />
            <TextField
              name="buyerName"
              label={t('website:jobDetails.fields.company')}
              type="text"
              wrapperSx={{
                '& .MuiInputBase-root-MuiOutlinedInput-root.Mui-disabled, .Mui-disabled': {
                  backgroundColor: 'background.jobAd',
                  border: 'none!important',
                  pl: 0,
                },
                '& .MuiInputBase-input.Mui-disabled': {
                  px: '0!important',
                  py: '4px!important',
                  border: "none",
                }
              }}
              disabled
            />
            <TextField
              name="maxRate"
              label={t('website:jobDetails.fields.maxRate')}
              disabled
              type="text"
              InputProps={{
                endAdornment: (
                  <InputAdornment
                    position="end"
                  >
                    SEK/h
                  </InputAdornment>
                )
              }}
              wrapperSx = {({palette}) => ({
                '& .MuiInputBase-root-MuiOutlinedInput-root.Mui-disabled, .Mui-disabled': {
                  backgroundColor: `${palette.background.jobAd}!important`,
                  border: 'none!important',
                  pl: 0,
                },
                '& .MuiInputBase-input.Mui-disabled': {
                  px: '0!important',
                  py: '4px!important',
                },
                width: 150,
              })}
            />
            <TextField
              name="public_id"
              label={t('website:jobDetails.fields.uniqueId')}
              type="text"
              wrapperSx={{
                '& .MuiInputBase-root-MuiOutlinedInput-root.Mui-disabled, .Mui-disabled': {
                  backgroundColor: 'background.jobAd',
                  border: 'none',
                  pl: 0,
                },
                '& .MuiInputBase-input.Mui-disabled': {
                  px: '0!important',
                  py: '4px!important',
                  border: "none",
                }
              }}
              disabled
            />
            <TextField
              name="type"
              label={t('website:jobDetails.fields.type')}
              type="text"
              wrapperSx={{
                '& .MuiInputBase-root-MuiOutlinedInput-root.Mui-disabled, .Mui-disabled': {
                  backgroundColor: 'background.jobAd',
                  border: 'none',
                  pl: 0,
                },
                '& .MuiInputBase-input.Mui-disabled': {
                  px: '0!important',
                  py: '4px!important',
                  border: "none",
                }
              }}
              disabled
            />
            <TextField
              name="hoursPerWeek"
              label={t('website:jobDetails.fields.hoursPerWeek')}
              type="text"
              wrapperSx={{
                '& .MuiInputBase-root-MuiOutlinedInput-root.Mui-disabled, .Mui-disabled': {
                  backgroundColor: 'background.jobAd',
                  border: 'none',
                  pl: 0,
                },
                '& .MuiInputBase-input.Mui-disabled': {
                  px: '0!important',
                  py: '4px!important',
                  border: "none",
                }
              }}
              disabled
            />
            {methods.getValues("locations") && methods.getValues("locations").length > 0 && (
              <TextField
                name="locations"
                label={t('website:jobDetails.fields.locations')}
                disabled
                type="text"
                fullWidth
                isChip
                locations
                chipSx={{
                  backgroundColor: 'background.jobAd',
                  '& .MuiChip-label': {
                    px: 0,
                  },
                  height: 24
                }}
                parentSx={{
                  flexDirection: "column",
                  alignItems: "flex-start",
                  gap: 0,
                }}
              />
            )}
            <StaticLocationMap url={jobAd?.static_map_url} alt={t('website:jobDetails.mapAlt')} />
            {methods.getValues("competenceAreas") && methods.getValues("competenceAreas").length > 0 && (
              <TextField
                name="competenceAreas"
                label={t('website:jobDetails.fields.competenceAreas')}
                disabled
                type="text"
                fullWidth
                isChip
                chipSx={{
                  backgroundColor: 'background.jobAd',
                  '& .MuiChip-label': {
                    pl: 0,
                  },
                }}
              />
            )}
            <AdditionalPeriods
              initialPeriod={initialPeriod}
              additionalPeriods={additionalPeriods}
              handleAdditionalPeriods={null}
              readOnly
              tender
              showAsList
              listLabel={t('website:jobDetails.periods.dates')}
              getPeriodLabel={(index) => index === 0 ? t('website:jobDetails.periods.initial') : t('website:jobDetails.periods.option', { index })}
            />
          </Stack>
          {jobAd?.supplier_name?.length > 0 && jobAd?.supplier_description?.length > 0 && (
            <SmallProfile company_name={jobAd?.supplier_name} square_logo_url={jobAd?.supplier_logo_url} banner_logo_url={jobAd?.supplier_banner_url} description={jobAd?.supplier_description} slug={jobAd?.supplier_slug} company_id={jobAd?.supplier_id} company_type='supplier' />
          )}
          <Stack
            sx={{
              backgroundColor: "background.jobAd",
              border: "1px solid",
              borderColor: "border.jobAd",
              borderRadius: 1,
              p: 2,
              alignItems: "center",
              gap: 1.5
            }}
          >
            <Typography
              variant='h5'
              sx={{
                color: "grey.900"
              }}
            >
              {t('website:jobDetails.share')}
            </Typography>
            <LinkedinShareButton url={`${DELEGON_URL}${paths.public.jobAdsDetailsView(jobAd?.slug, lang)}`}>
              <LinkedinIcon size={26} borderRadius={16} bgStyle={{fill: `${theme.palette.background.primary}`}}/>
            </LinkedinShareButton>
          </Stack>
        </Stack>
      </Stack>
    </FormProvider>
  );
};
